import { z } from 'zod';
/**
 * Per-client bidding mode (admin deals & deposits — phase 1, SC-1).
 *
 * Every client bids in exactly ONE of two modes, set by an admin on the client
 * card («Режим ставок»):
 *
 *  - `deposit` — the client's CHF deposit times a multiplier (×5, ×10 or ×15) is
 *    the ceiling on the CHF they may have COMMITTED at once. There is no count
 *    limit in this mode: the money is the limit.
 *  - `trusted` — no deposit rule at all; the client may hold at most `bidLimit`
 *    simultaneously ACTIVE bids. Raising a bid they already hold never counts
 *    as a new one.
 *
 * This module is PURE — no Nest, no Mongo — and it is the one place these
 * formulas live. apps/api enforces them at bid acceptance and reports them from
 * `GET /api/users/me/exposure`; apps/admin and apps/auction only display what
 * the API returns. Money is whole-or-fractional CHF as a plain `number`, the
 * same convention as `exposure.ts`.
 */
/** The two bidding modes. */
export const BiddingModeKindSchema = z.enum(['deposit', 'trusted']);
export const BIDDING_MODE_KINDS = BiddingModeKindSchema.options;
/**
 * Deposit multipliers an admin may choose — the ×5 / ×10 / ×15 switch (owner
 * decision 2026-09-28; was ×2 / ×4 / ×6).
 */
export const DepositMultiplierSchema = z.union([z.literal(5), z.literal(10), z.literal(15)]);
export const DEPOSIT_MULTIPLIERS = [5, 10, 15];
/**
 * The retired ×2 / ×4 / ×6 values mapped onto their replacements. `users.bidding`
 * rows written before the switch still carry them until
 * `apps/api/scripts/migrate-deposit-multiplier.ts` rewrites them; reading them
 * through this map keeps those clients in deposit mode in the meantime instead
 * of silently parsing them as "no mode set".
 */
export const LEGACY_DEPOSIT_MULTIPLIER_MAP = {
    2: 5,
    4: 10,
    6: 15,
};
/** A STORED multiplier: the current values, plus the legacy ones normalized. */
export const StoredDepositMultiplierSchema = z.preprocess((value) => typeof value === 'number' ? (LEGACY_DEPOSIT_MULTIPLIER_MAP[value] ?? value) : value, DepositMultiplierSchema);
/** Bounds of a trusted client's simultaneous-bid limit, as the PUT body accepts it. */
export const TRUSTED_BID_LIMIT_MIN = 1;
export const TRUSTED_BID_LIMIT_MAX = 50;
/**
 * `users.bidding` — the stored mode. BOTH `multiplier` and `bidLimit` are kept
 * whatever the mode, so switching back restores the last value the admin set
 * rather than a default. `bidLimit` here is authoritative; the legacy top-level
 * `users.bidLimit` is a mirror written in the same update.
 */
export const ClientBiddingSchema = z.object({
    mode: BiddingModeKindSchema,
    multiplier: StoredDepositMultiplierSchema,
    bidLimit: z.number().int().min(0),
    /** Admin userId who made the last change. */
    changedBy: z.string(),
    changedAt: z.coerce.date(),
});
/**
 * `changedBy` of a mode the PLATFORM wrote at sign-up, as opposed to an admin's
 * userId. Not a user id on purpose: the admin card looks the actor up and
 * falls back to «змінено {date}» when nobody is found.
 */
export const DEFAULT_BIDDING_ACTOR = 'system:default-deposit';
/**
 * Owner decision 2026-10-11: EVERY new client starts in deposit mode. Before
 * it a sign-up wrote only the legacy `bidLimit: 10`, so a client stayed under
 * the legacy exposure rule — which only WARNS unless EXPOSURE_ENFORCED is on —
 * until an admin picked a mode by hand; one client bid past their deposit that
 * way. The multiplier is decision (e)'s ×10 (the same the backfill applies),
 * the bid limit is the legacy registration default, kept so a later switch to
 * trusted restores a sane count.
 */
export const DEFAULT_CLIENT_BIDDING_MULTIPLIER = 10;
export const DEFAULT_CLIENT_BID_LIMIT = 10;
/** The `users.bidding` a new client is created with. Pure. */
export function defaultClientBidding(now = new Date()) {
    return {
        mode: 'deposit',
        multiplier: DEFAULT_CLIENT_BIDDING_MULTIPLIER,
        bidLimit: DEFAULT_CLIENT_BID_LIMIT,
        changedBy: DEFAULT_BIDDING_ACTOR,
        changedAt: now,
    };
}
/**
 * `PUT /api/admin/users/:userId/bidding-mode` body. The mode decides which one
 * parameter is accepted; anything else is refused (`.strict()`), so a body that
 * sends a multiplier with `trusted` is a 400, not a silently ignored field.
 */
export const BiddingModePutSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('deposit'), multiplier: DepositMultiplierSchema }).strict(),
    z
        .object({
        mode: z.literal('trusted'),
        bidLimit: z.number().int().min(TRUSTED_BID_LIMIT_MIN).max(TRUSTED_BID_LIMIT_MAX),
    })
        .strict(),
]);
/**
 * One row of `client_bidding_journal` — append-only, written in the same
 * transaction as the `users.bidding` update. `previous: null` = the first mode
 * the client ever had (backfill or first admin choice).
 *
 * `depositKeptAsPrepaymentChf` — owner decision (d), 2026-09-28: a client
 * switched TO trusted keeps whatever deposit they hold (no auto-refund); it
 * stays in `users.biddingPower` and is applied to their next car invoice as a
 * prepayment. The row records the CHF held at the switch; absent on every
 * other change (and on rows written before the decision).
 */
export const BiddingJournalEntrySchema = z.object({
    userId: z.string(),
    previous: ClientBiddingSchema.nullable(),
    next: ClientBiddingSchema,
    changedBy: z.string(),
    changedAt: z.coerce.date(),
    depositKeptAsPrepaymentChf: z.number().finite().positive().optional(),
});
/** Round to the cent — the unit every CHF figure on screen is shown in. */
function roundChf(value) {
    return Math.round(value * 100) / 100;
}
/**
 * THE buying power of one client. Pure.
 *
 * `committedChf` must already EXCLUDE the bid being raised (raising one's own
 * bid swaps the old amount for the new one; it is not double-counted) — the
 * same contract as {@link evaluateExposure} in `exposure.ts`.
 */
export function resolveBuyingPower(bidding, depositChf, committedChf, activeBidCount) {
    if (bidding.mode === 'trusted') {
        return {
            mode: 'trusted',
            bidLimit: bidding.bidLimit,
            activeBidCount,
            remainingBids: Math.max(0, bidding.bidLimit - activeBidCount),
        };
    }
    // A negative stored balance is corrupt data, never negative credit: it
    // yields no ceiling rather than a negative one.
    const capChf = roundChf(Math.max(0, depositChf) * bidding.multiplier);
    const rawFree = roundChf(capChf - committedChf);
    return {
        mode: 'deposit',
        depositChf,
        multiplier: bidding.multiplier,
        capChf,
        committedChf,
        freeChf: Math.max(0, rawFree),
        overCeiling: rawFree < 0,
    };
}
/** Why a bid is refused. `null` when it is allowed. */
export const BidRefusalReasonSchema = z.enum(['NO_DEPOSIT', 'OVER_CEILING', 'BID_COUNT']);
/**
 * Would this bid be accepted under the client's mode? Pure.
 *
 * - deposit: no deposit → `NO_DEPOSIT`; `amountChf > freeChf` → `OVER_CEILING`
 *   (an amount that exactly fills the free room is allowed).
 * - trusted: a NEW bid when `activeBidCount >= bidLimit` → `BID_COUNT`; raising
 *   a bid the client already holds is never refused for the count.
 */
export function evaluateBid(bp, amountChf, options) {
    if (bp.mode === 'deposit') {
        if (bp.depositChf <= 0)
            return { allowed: false, reason: 'NO_DEPOSIT' };
        if (amountChf > bp.freeChf)
            return { allowed: false, reason: 'OVER_CEILING' };
        return { allowed: true, reason: null };
    }
    if (options.isNewBid && bp.activeBidCount >= bp.bidLimit) {
        return { allowed: false, reason: 'BID_COUNT' };
    }
    return { allowed: true, reason: null };
}
/** The finance tab's «Стан» column. */
export const BuyingPowerStateSchema = z.enum([
    'can_bid',
    'over_ceiling',
    'awaiting_deposit',
    'refund_requested',
]);
export const BUYING_POWER_STATES = BuyingPowerStateSchema.options;
/**
 * One state per client, in precedence order:
 *
 * 1. `refund_requested` — the client asked for their deposit back; that is
 *    what the operator must act on, whatever else is true.
 * 2. deposit mode with no deposit → `awaiting_deposit`.
 * 3. no room left for any new bid → `over_ceiling`: deposit mode when
 *    `freeChf` is 0 (at OR over the ceiling — at it, no positive bid fits),
 *    trusted mode when no bid slot remains.
 * 4. otherwise `can_bid`.
 */
export function buyingPowerState(bp, options) {
    if (options.refundRequested)
        return 'refund_requested';
    if (bp.mode === 'deposit') {
        if (bp.depositChf <= 0)
            return 'awaiting_deposit';
        return bp.freeChf <= 0 ? 'over_ceiling' : 'can_bid';
    }
    return bp.remainingBids <= 0 ? 'over_ceiling' : 'can_bid';
}
/**
 * How much of the deposit an invoice consumes as a prepayment. Pure, rounded
 * to 0.01. A deposit larger than the invoice leaves the remainder on deposit;
 * a smaller one is consumed whole. A negative deposit or total yields a zero
 * prepayment; `depositAfterChf` then carries the deposit through unchanged.
 */
export function prepaymentFor(depositChf, totalChf) {
    const prepaymentChf = roundChf(Math.min(Math.max(0, depositChf), Math.max(0, totalChf)));
    return { prepaymentChf, depositAfterChf: roundChf(depositChf - prepaymentChf) };
}
/**
 * A client may ask for their deposit back only when nothing depends on it:
 * no active bid, no unpaid invoice, and a positive deposit to return.
 */
export function canRequestRefund(input) {
    return input.activeBidCount === 0 && input.unpaidInvoiceCount === 0 && input.depositChf > 0;
}
/**
 * i18n keys of «Клієнт бачить: …» on the client card. The strings are added
 * in `packages/i18n/src/{uk,en}/clients.json` (phase 9); the params below are
 * the interpolation arguments.
 */
export const CLIENT_SEES_LABEL_KEYS = {
    deposit: 'clients.biddingMode.clientSees.deposit',
    noDeposit: 'clients.biddingMode.clientSees.noDeposit',
    trusted: 'clients.biddingMode.clientSees.trusted',
};
/**
 * What the client is shown about their own buying power, as an i18n key plus
 * numeric args — never a rendered caption, so the admin UI can show it in the
 * operator's language.
 */
export function clientSeesLabel(bp) {
    if (bp.mode === 'trusted') {
        return {
            key: CLIENT_SEES_LABEL_KEYS.trusted,
            params: { remainingBids: bp.remainingBids, bidLimit: bp.bidLimit },
        };
    }
    if (bp.depositChf <= 0) {
        return { key: CLIENT_SEES_LABEL_KEYS.noDeposit, params: { multiplier: bp.multiplier } };
    }
    return {
        key: CLIENT_SEES_LABEL_KEYS.deposit,
        params: { freeChf: bp.freeChf, capChf: bp.capChf, multiplier: bp.multiplier },
    };
}
//# sourceMappingURL=bidding-mode.js.map